<!--
  One audience (an Industry or a Topic).

  The route param is "{targetType}-{targetId}" (e.g. /marketing/audiences/2-15) because an audience is
  identified by the same pair a campaign target uses, not by a surrogate id.
-->
<template>
  <v-container fluid>
    <div class="d-flex align-center flex-wrap mb-4">
      <v-btn
        icon
        class="mr-2"
        title="Back to audiences"
        @click="$router.push('/marketing/audiences')"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <h2 class="text-h5 mr-3">
        {{ overview.name || 'Audience' }}
      </h2>

      <v-chip
        v-if="overview.typeName"
        small
        outlined
        class="mr-2"
      >
        {{ overview.typeName }}
      </v-chip>

      <v-chip
        v-if="plan"
        small
        :color="planStatus.color"
        dark
        class="mr-2"
      >
        {{ planStatus.label }}
      </v-chip>

      <v-btn
        v-if="overview.url"
        small
        text
        color="mcsprimary"
        :href="overview.url"
        target="_blank"
        rel="noopener"
      >
        View page
        <v-icon
          right
          small
        >
          mdi-open-in-new
        </v-icon>
      </v-btn>

      <v-spacer />

      <v-btn
        icon
        title="Refresh"
        :loading="loading"
        @click="load"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>

      <v-btn
        color="primary"
        class="ml-2"
        :disabled="generating"
        @click="$refs.briefDialog.open()"
      >
        <v-icon left>
          mdi-auto-fix
        </v-icon>
        {{ plan ? 'Regenerate plan' : 'Build plan' }}
      </v-btn>

      <v-btn
        color="secondary"
        outlined
        class="ml-2"
        title="Create a campaign for this audience"
        @click="newCampaign"
      >
        <v-icon left>
          mdi-plus
        </v-icon>
        Campaign
      </v-btn>
    </div>

    <GenerationBanner
      :status="generationStatus"
      :server-message="generationMessage"
      @retry="generate(lastBrief)"
    />

    <v-alert
      v-if="planHealthNote"
      :type="plan.health === $enums.AUDIENCE.PLAN_HEALTH_IDS.DARK ? 'error' : 'warning'"
      text
      border="left"
      class="mb-4"
    >
      {{ planHealthNote }}
    </v-alert>

    <v-alert
      v-if="majorGapCount"
      type="warning"
      text
      border="left"
      class="mb-4"
    >
      {{ majorGapCount }} major gap{{ majorGapCount === 1 ? '' : 's' }} block marketing to this audience
      properly — see Content gaps.
    </v-alert>

    <v-card>
      <v-tabs
        v-model="tab"
        show-arrows
      >
        <v-tab>Overview</v-tab>
        <v-tab>Plan</v-tab>
        <v-tab>
          Suggestions
          <v-chip
            v-if="pendingCount"
            x-small
            class="ml-2"
            color="primary"
            dark
          >
            {{ pendingCount }}
          </v-chip>
        </v-tab>
        <v-tab>
          Content gaps
          <v-chip
            v-if="gapCount"
            x-small
            class="ml-2"
            :color="majorGapCount ? 'error' : 'grey'"
            dark
          >
            {{ gapCount }}
          </v-chip>
        </v-tab>
        <v-tab>Templates</v-tab>
        <v-tab>Roadmap</v-tab>
      </v-tabs>

      <v-divider />

      <v-card-text>
        <v-skeleton-loader
          v-if="loading && !overview.name"
          type="article, table"
        />

        <v-tabs-items
          v-else
          v-model="tab"
        >
          <v-tab-item>
            <AudienceOverview
              :overview="overview"
              :loading="loading"
            />
          </v-tab-item>

          <v-tab-item>
            <v-skeleton-loader
              v-if="generating && !plan"
              type="article"
            />
            <template v-else>
              <v-card
                v-if="plan && plan.positioning"
                outlined
                class="mb-4"
              >
                <v-card-title class="text-subtitle-1">
                  Who they are
                </v-card-title>
                <v-card-text class="text-body-2">
                  {{ plan.positioning }}
                </v-card-text>
              </v-card>

              <div
                v-if="!plan"
                class="text-caption grey--text mb-4"
              >
                No plan yet. "Build plan" drafts one from this audience's real numbers — customers, print
                revenue, content coverage and what's already booked.
              </div>

              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <StrategyPanel
                    title="Existing customers"
                    field="existingCustomerPlan"
                    :section="plan ? plan.existingCustomerPlan : null"
                    :plan="plan || {}"
                    :items="planItems"
                    @focus-item="focusItem"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <StrategyPanel
                    title="New customers"
                    field="newCustomerPlan"
                    :section="plan ? plan.newCustomerPlan : null"
                    :plan="plan || {}"
                    :items="planItems"
                    @focus-item="focusItem"
                  />
                </v-col>
              </v-row>
            </template>
          </v-tab-item>

          <v-tab-item>
            <Suggestions
              :items="planItems"
              :has-plan="!!plan"
              :plan-id="plan ? plan.id : 0"
              :focus-id="focusItemId"
              @changed="load"
            />
          </v-tab-item>

          <v-tab-item>
            <ContentGaps
              :target-type="targetType"
              :target-id="targetId"
              :gaps="overview.gaps || {}"
            />
          </v-tab-item>

          <v-tab-item>
            <AudienceTemplates
              :target-type="targetType"
              :target-id="targetId"
            />
          </v-tab-item>

          <v-tab-item>
            <AudienceRoadmap :roadmap="overview.roadmap || {}" />
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
    </v-card>

    <BriefDialog
      ref="briefDialog"
      :regenerating="!!plan"
      @confirm="generate"
    />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import AudienceOverview from '@/components/marketing/audiences/Overview';
import ContentGaps from '@/components/marketing/audiences/ContentGaps';
import AudienceRoadmap from '@/components/marketing/audiences/Roadmap';
import AudienceTemplates from '@/components/marketing/audiences/Templates';
import GenerationBanner from '@/components/marketing/audiences/GenerationBanner';
import StrategyPanel from '@/components/marketing/audiences/StrategyPanel';
import Suggestions from '@/components/marketing/audiences/Suggestions';
import BriefDialog from '@/components/marketing/audiences/BriefDialog';

const TAB_KEYS = ['overview', 'plan', 'suggestions', 'gaps', 'templates', 'roadmap'];

// Poll cadence while an AI draft is in flight. Capped so a job that dies without reporting can't leave the
// page polling forever.
const POLL_MS = 4000;
const MAX_POLLS = 75; // ≈5 minutes

export default {
  name: 'AudienceDetail',
  components: {
    AudienceOverview,
    ContentGaps,
    AudienceRoadmap,
    AudienceTemplates,
    GenerationBanner,
    StrategyPanel,
    Suggestions,
    BriefDialog
  },
  data() {
    return {
      tab: 0,
      loading: false,
      pollTimer: null,
      pollAttempts: 0,
      // Held locally so the banner can appear the instant the POST succeeds, rather than after the first
      // poll returns 4s later — that dead air reads as "nothing happened".
      localGenerationStatus: null,
      lastBrief: '',
      // The suggestion card a click on the plan's cadence is pointing at. 0 when nothing is being singled out.
      focusItemId: 0
    };
  },
  computed: {
    ...mapGetters({ overview: 'audiences/overview' }),
    // "{targetType}-{targetId}" — see the file header.
    targetType() {
      return parseInt((this.$route.params.id || '').split('-')[0], 10) || 0;
    },
    targetId() {
      return parseInt((this.$route.params.id || '').split('-')[1], 10) || 0;
    },
    plan() {
      return this.overview.plan || null;
    },
    planItems() {
      return this.plan ? this.plan.items || [] : [];
    },
    planStatus() {
      if (!this.plan) return { label: '—', color: 'grey' };

      // Runway first — see List.vue's planChipFor. A plan that has run out of booked work says "Active"
      // for ever otherwise, which is the one state the header ought to be shouting about.
      const health = this.$enums.AUDIENCE.PLAN_HEALTH[this.plan.health];
      if (health) return health;

      return this.$enums.AUDIENCE.PLAN_STATUS[this.plan.status] || { label: '—', color: 'grey' };
    },
    /** The "and here is why the chip says that" line under the header. Empty while the plan has runway. */
    planHealthNote() {
      if (!this.plan || !this.$enums.AUDIENCE.PLAN_HEALTH[this.plan.health]) return '';

      const dark = this.plan.health === this.$enums.AUDIENCE.PLAN_HEALTH_IDS.DARK;
      const last = this.plan.lastBookedSendDate
        ? this.$options.filters.normalDate(this.plan.lastBookedSendDate)
        : '';
      return dark
        ? `The last planned send went out ${last} and nothing is booked behind it — redraft the plan to give this audience its next round.`
        : `The last planned send is ${last} and nothing is booked behind it. Redraft the plan now so the next round has time to schedule.`;
    },
    pendingCount() {
      return this.planItems.filter(
        (i) => i.status === this.$enums.AUDIENCE.ITEM_STATUS_IDS.SUGGESTED
      ).length;
    },
    generationStatus() {
      if (this.localGenerationStatus !== null) return this.localGenerationStatus;
      return this.plan ? this.plan.generationStatus : this.$enums.AUDIENCE.GENERATION_STATUS.IDLE;
    },
    generationMessage() {
      return this.plan ? this.plan.generationMessage : '';
    },
    generating() {
      const s = this.$enums.AUDIENCE.GENERATION_STATUS;
      return this.generationStatus === s.QUEUED || this.generationStatus === s.RUNNING;
    },
    gapCount() {
      return ((this.overview.gaps || {}).items || []).length;
    },
    majorGapCount() {
      return ((this.overview.gaps || {}).items || []).filter((g) => g.severity === 3).length;
    }
  },
  watch: {
    tab(value) {
      // Leaving Suggestions by hand drops the singled-out card — it was a jump to one item, not a filter the
      // marketer set, so it must not still be applied when they come back through the tab bar.
      if (TAB_KEYS[value] !== 'suggestions') this.focusItemId = 0;
      this.syncQuery();
    },
    focusItemId() {
      this.syncQuery();
    }
  },
  mounted() {
    const index = TAB_KEYS.indexOf(this.$route.query.tab);
    if (index >= 0) this.tab = index;

    const item = parseInt(this.$route.query.item, 10);
    if (item > 0 && TAB_KEYS[this.tab] === 'suggestions') this.focusItemId = item;

    this.load().then(() => {
      // Navigating back into a mid-flight draft picks the poll back up.
      if (this.generating) this.startPolling();
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange);
  },
  beforeDestroy() {
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    // Keeps the open tab and the singled-out card in the URL, so a refresh, a back-button or a shared link
    // lands in the same place. One writer for both, built from component state each time, so switching tabs
    // and focusing a card can't race each other into a half-written query.
    syncQuery() {
      const query = { ...this.$route.query, tab: TAB_KEYS[this.tab] };
      if (this.focusItemId) query.item = String(this.focusItemId);
      else delete query.item;

      if (query.tab === this.$route.query.tab && query.item === this.$route.query.item) return;
      this.$router.replace({ query }).catch(() => {});
    },
    // A touch in the plan's cadence was clicked. Tab first, then the id: the tab watcher clears the focus on
    // any move AWAY from Suggestions, and both assignments land before it runs.
    focusItem(itemId) {
      this.tab = TAB_KEYS.indexOf('suggestions');
      this.focusItemId = itemId;
    },
    load() {
      if (!this.targetId) {
        this.$router.replace('/marketing/audiences');
        return Promise.resolve();
      }

      this.loading = true;
      return this.$store
        .dispatch('audiences/getOverview', {
          targetType: this.targetType,
          targetId: this.targetId
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loading = false;
        });
    },
    generate(brief) {
      this.lastBrief = brief || '';
      this.$store
        .dispatch('audiences/generatePlan', {
          targetType: this.targetType,
          targetId: this.targetId,
          brief: this.lastBrief
        })
        .then(() => {
          this.localGenerationStatus = this.$enums.AUDIENCE.GENERATION_STATUS.QUEUED;
          this.startPolling();
        })
        .catch((error) => this.logError(error));
    },
    startPolling() {
      this.stopPolling();
      this.pollAttempts = 0;
      this.pollTimer = setInterval(this.poll, POLL_MS);
    },
    stopPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    onVisibilityChange() {
      // Catch up immediately when the tab comes back, instead of waiting out the interval.
      if (document.visibilityState === 'visible' && this.pollTimer) this.poll();
    },
    poll() {
      // Don't burn requests on a background tab.
      if (document.visibilityState !== 'visible') return;

      if (++this.pollAttempts > MAX_POLLS) {
        this.stopPolling();
        return;
      }

      this.$store
        .dispatch('audiences/getPlanStatus', {
          targetType: this.targetType,
          targetId: this.targetId
        })
        .then((status) => {
          const s = this.$enums.AUDIENCE.GENERATION_STATUS;
          if (status.generationStatus === s.QUEUED || status.generationStatus === s.RUNNING) {
            this.localGenerationStatus = status.generationStatus;
            return;
          }

          // Settled — drop the local override and reload so the real plan renders.
          this.stopPolling();
          this.localGenerationStatus = null;
          this.load().then(() => {
            if (status.generationStatus === s.FAILED) {
              this.$root.$snackbarError(status.message || 'The plan could not be generated.');
            } else {
              this.$root.$snackbarSuccess('Plan ready');
            }
          });
        })
        .catch((error) => {
          this.stopPolling();
          this.logError(error);
        });
    },
    // Seeds a new campaign already pointed at this audience.
    newCampaign() {
      this.$router.push(
        `/marketing/campaigns/0?type=${this.$enums.MARKETING.CAMPAIGN_TYPE.EMAIL}` +
          `&targetType=${this.targetType}&targetId=${this.targetId}`
      );
    }
  }
};
</script>
